const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const BUNDLE_CHANGE_EVENT = 'fursera:bundle:change';
const PAYPAL_STATE_KEY = '__furseraPayPalMessagesState';

function getPayPalState() {
  if (!window[PAYPAL_STATE_KEY]) {
    window[PAYPAL_STATE_KEY] = {
      clientId: null,
      environment: null,
      buyerCountry: null,
      currencyCode: null,
      corePromise: null,
      integrationPromise: null,
    };
  }

  return window[PAYPAL_STATE_KEY];
}

function loadPayPalCore(environment) {
  const state = getPayPalState();
  if (window.paypal?.createInstance) return Promise.resolve(window.paypal);
  if (state.corePromise) return state.corePromise;

  const source =
    environment === 'sandbox'
      ? 'https://www.sandbox.paypal.com/web-sdk/v6/core'
      : 'https://www.paypal.com/web-sdk/v6/core';

  state.corePromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-fursera-paypal-core]');
    const script = existingScript || document.createElement('script');

    const handleLoad = () => {
      if (window.paypal?.createInstance) {
        resolve(window.paypal);
      } else {
        reject(new Error('PayPal Web SDK loaded without createInstance'));
      }
    };
    const handleError = () => reject(new Error('PayPal Web SDK failed to load'));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = source;
      script.async = true;
      script.dataset.furseraPaypalCore = environment;
      document.head.append(script);
    }
  });

  return state.corePromise;
}

async function getPayPalIntegration(clientId, environment, buyerCountry, currencyCode) {
  const state = getPayPalState();

  if (
    state.integrationPromise &&
    (state.clientId !== clientId ||
      state.environment !== environment ||
      state.buyerCountry !== buyerCountry ||
      state.currencyCode !== currencyCode)
  ) {
    throw new Error('Only one PayPal Client ID can be initialized on a page');
  }

  if (!state.integrationPromise) {
    state.clientId = clientId;
    state.environment = environment;
    state.buyerCountry = buyerCountry;
    state.currencyCode = currencyCode;
    state.integrationPromise = loadPayPalCore(environment).then(async (paypal) => {
      const sdkInstance = await paypal.createInstance({
        clientId,
        components: ['paypal-messages'],
      });
      const messages = sdkInstance.createPayPalMessages({
        currencyCode,
        ...(buyerCountry ? { buyerCountry } : {}),
      });
      const learnMore = await messages.createLearnMore({ presentationMode: 'AUTO' });
      return { messages, learnMore };
    });
  }

  return state.integrationPromise;
}

class FurseraPayPalMessage extends HTMLElement {
  #messageElement;
  #eventTarget;
  #renderSequence = 0;
  #renderTimer;
  #visibilityTimer;

  connectedCallback() {
    this.#messageElement = this.querySelector('paypal-message');
    if (!this.#messageElement) return;

    if (this.dataset.paypalPlacement === 'product') {
      this.#eventTarget = this.closest('.shopify-section, dialog');
      this.#eventTarget?.addEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect);
      this.#eventTarget?.addEventListener(BUNDLE_CHANGE_EVENT, this.#handleBundleChange);
    } else if (this.dataset.paypalPlacement === 'cart') {
      this.#eventTarget = document;
      document.addEventListener(CART_LINES_UPDATE_EVENT, this.#handleCartUpdate);
    }

    this.#scheduleRender();
  }

  disconnectedCallback() {
    clearTimeout(this.#renderTimer);
    clearTimeout(this.#visibilityTimer);
    this.#renderSequence += 1;
    if (this.dataset.paypalPlacement === 'product') {
      this.#eventTarget?.removeEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect);
      this.#eventTarget?.removeEventListener(BUNDLE_CHANGE_EVENT, this.#handleBundleChange);
    } else if (this.dataset.paypalPlacement === 'cart') {
      document.removeEventListener(CART_LINES_UPDATE_EVENT, this.#handleCartUpdate);
    }
  }

  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail, variant }) => {
        const resource = variant || detail?.variant || detail?.resource;
        const price = resource?.price;
        const amount =
          typeof price === 'object'
            ? price?.amount
            : Number.isFinite(Number(price))
              ? Number(price) / 100
              : null;
        const currency =
          (typeof price === 'object' ? price?.currencyCode : null) ||
          detail?.currencyCode ||
          this.dataset.paypalCurrency;
        if (amount) this.#setAmount(amount, currency);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#hide('product-update-error');
      });
  };

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const total = cart?.cost?.totalAmount;
        if (total?.amount) this.#setAmount(total.amount, total.currencyCode);
        else this.#hide('empty-cart');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#hide('cart-update-error');
      });
  };

  #handleBundleChange = (event) => {
    const amount = event.detail?.amount;
    const currency = event.detail?.currencyCode;
    if (amount) this.#setAmount(amount, currency);
  };

  #setAmount(amount, currency) {
    const normalizedCurrency = String(currency || this.dataset.paypalCurrency || '').toUpperCase();
    this.dataset.paypalAmount = String(amount);
    if (normalizedCurrency) this.dataset.paypalCurrency = normalizedCurrency;
    this.#messageElement?.setAttribute('amount', String(amount));
    if (normalizedCurrency) this.#messageElement?.setAttribute('currency-code', normalizedCurrency);
    this.#scheduleRender();
  }

  #scheduleRender() {
    clearTimeout(this.#renderTimer);
    this.#renderTimer = setTimeout(() => this.#render(), 80);
  }

  async #render() {
    const clientId = this.dataset.paypalClientId?.trim();
    const environment = this.dataset.paypalEnvironment === 'sandbox' ? 'sandbox' : 'production';
    const amount = this.dataset.paypalAmount?.trim();
    const currencyCode = this.dataset.paypalCurrency?.trim()?.toUpperCase();
    const buyerCountry = this.dataset.paypalBuyerCountry?.trim()?.toUpperCase();
    const minimumAmount = Number(this.dataset.paypalMinimum || 0);

    if (!clientId || !amount || !currencyCode || Number(amount) <= 0 || !this.#messageElement) {
      this.#hide('invalid-configuration');
      return;
    }

    if (Number(amount) < minimumAmount) {
      this.#hide('below-minimum');
      return;
    }

    const sequence = ++this.#renderSequence;
    this.hidden = false;
    this.dataset.paypalState = 'loading';

    try {
      const { learnMore } = await getPayPalIntegration(
        clientId,
        environment,
        buyerCountry,
        currencyCode,
      );
      if (sequence !== this.#renderSequence || !this.isConnected) return;

      this.#messageElement.toggleAttribute('auto-bootstrap', true);
      this.#messageElement.setAttribute('amount', amount);
      this.#messageElement.setAttribute('currency-code', currencyCode);
      this.#waitForVisibleMessage(sequence);

      if (!this.dataset.paypalLearnMoreReady) {
        this.#messageElement.addEventListener('paypal-message-click', (event) => {
          event.preventDefault();
          learnMore.open(event.detail.config);
        });
        this.dataset.paypalLearnMoreReady = 'true';
      }
    } catch (error) {
      console.warn('[paypal-message] Pay Later message unavailable:', error);
      if (sequence === this.#renderSequence) this.#hide('unavailable');
    }
  }

  #waitForVisibleMessage(sequence, startedAt = performance.now(), visibleSince = null) {
    clearTimeout(this.#visibilityTimer);
    if (sequence !== this.#renderSequence || !this.isConnected || !this.#messageElement) return;

    const messageRect = this.#messageElement.getBoundingClientRect();
    if (messageRect.height > 0) {
      const stableSince = visibleSince ?? performance.now();
      if (performance.now() - stableSince >= 300) {
        this.hidden = false;
        this.dataset.paypalState = 'ready';
        return;
      }
      this.#visibilityTimer = setTimeout(
        () => this.#waitForVisibleMessage(sequence, startedAt, stableSince),
        50,
      );
      return;
    }

    if (performance.now() - startedAt >= 3000) {
      this.#hide('unavailable');
      return;
    }

    this.#visibilityTimer = setTimeout(
      () => this.#waitForVisibleMessage(sequence, startedAt, null),
      50,
    );
  }

  #hide(state) {
    clearTimeout(this.#visibilityTimer);
    this.#renderSequence += 1;
    this.hidden = true;
    this.dataset.paypalState = state;
  }
}

if (!customElements.get('fursera-paypal-message')) {
  customElements.define('fursera-paypal-message', FurseraPayPalMessage);
}
